import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Loader2, Play, Square } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import EvidenceTimeline from './EvidenceTimeline';
import AkroChatPanel from './AkroChatPanel';

export const SessionsTab = ({ activeSessionId, setActiveSessionId }) => {
  const [sessions, setSessions] = useState([]);
  const [suites, setSuites] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, su] = await Promise.all([tl.listSessions(), tl.listSuites()]);
      setSessions(s.items || []);
      setSuites(su.items || []);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { load(); }, []);

  const create = async (body) => {
    setBusy(true);
    try {
      const sess = await tl.createSession(body);
      setShowCreate(false);
      await load();
      setActiveSessionId(sess.test_session_id);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const endSession = async (id) => {
    try {
      await tl.updateSession(id, { status: 'completed' });
      toast.success('Session completed');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const active = sessions.find((s) => s.test_session_id === activeSessionId);

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]" data-testid="sessions-tab">
      <div className="col-span-3">
        <Card className="h-full flex flex-col">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Test Sessions</h3>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="session-new"><Plus className="w-3 h-3" /></Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1.5">
              {sessions.map((s) => (
                <button key={s.test_session_id} onClick={() => setActiveSessionId(s.test_session_id)}
                  className={cn(
                    'w-full text-left p-2 rounded border text-xs',
                    activeSessionId === s.test_session_id ? 'border-violet-500 bg-violet-500/5' : 'border-border hover:border-violet-500/50'
                  )}
                  data-testid={`session-row-${s.test_session_id}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Badge variant={s.status === 'active' ? 'default' : 'outline'} className="text-[9px] h-4">{s.status}</Badge>
                  </div>
                  <div className="font-medium truncate">{s.title}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(s.started_at).toLocaleString()}</div>
                </button>
              ))}
              {sessions.length === 0 && (
                <div className="text-center py-12 text-xs text-muted-foreground">
                  Start a new test session to begin capturing evidence.
                </div>
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>

      <div className="col-span-5">
        {active ? (
          <Card className="h-full flex flex-col">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  {active.status === 'active' && (
                    <span className="inline-flex items-center gap-1 text-red-500 text-[11px]">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> RECORDING
                    </span>
                  )}
                  {active.title}
                </h3>
                <p className="text-[11px] text-muted-foreground">Session {active.test_session_id}</p>
              </div>
              {active.status === 'active' && (
                <Button size="sm" variant="destructive" onClick={() => endSession(active.test_session_id)} data-testid="session-end">
                  <Square className="w-3 h-3 mr-1" /> End
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-hidden">
              <EvidenceTimeline sessionId={active.test_session_id} />
            </div>
          </Card>
        ) : (
          <Card className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Select or start a test session
          </Card>
        )}
      </div>

      <div className="col-span-4">
        {active ? (
          <AkroChatPanel testSessionId={active.test_session_id} contextHint={{ session_title: active.title, status: active.status }} />
        ) : (
          <Card className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Akro will appear when a session is active
          </Card>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="session-create-dialog">
          <DialogHeader><DialogTitle>Start test session</DialogTitle></DialogHeader>
          <CreateSessionForm onSubmit={create} disabled={busy} suites={suites} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CreateSessionForm = ({ onSubmit, disabled, suites }) => {
  const [title, setTitle] = useState('');
  const [suiteId, setSuiteId] = useState('');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Session title (e.g. 'Income ledger smoke')" data-testid="session-title-input" />
      <Select value={suiteId} onValueChange={setSuiteId}>
        <SelectTrigger data-testid="session-suite-select"><SelectValue placeholder="Suite (optional)" /></SelectTrigger>
        <SelectContent>
          {suites.map((s) => <SelectItem key={s.test_suite_id} value={s.test_suite_id}>{s.title}</SelectItem>)}
        </SelectContent>
      </Select>
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, test_suite_id: suiteId || undefined })} disabled={!title || disabled} data-testid="session-create-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Start</>}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default SessionsTab;
